import { supabase } from './client';

/**
 * Servicio para gestionar las solicitudes relacionadas con DJ usando Supabase
 */

/**
 * Obtiene los registros de canciones del DJ
 * @param {string} djViewId - ID de la vista del DJ (ahora form_id en Supabase)
 * @returns {Promise<Array>} - Arreglo con los registros encontrados
 */
export const getDJRecords = async (djViewId) => {
    try {
        // Primero obtenemos el ID interno del DJ usando el form_id (djViewId)
        const { data: djData, error: djError } = await supabase
            .from('djs')
            .select('id')
            .eq('form_id', djViewId)
            .maybeSingle();

        if (djError) throw djError;
        if (!djData) throw new Error('DJ no encontrado');

        const { data, error } = await supabase
            .from('song_requests')
            .select('*')
            .eq('dj_id', djData.id)
            .or('option.is.null,option.eq.pendiente')
            .order('created_at', { ascending: false });

        if (error) throw error;

        // Mapeamos para mantener compatibilidad con la estructura de Airtable
        return data.map(record => ({
            id: record.id,
            fields: {
                'Song Name': record.song_name,
                'Artist': record.artist,
                'Genre': record.genre,
                'Created': record.created_at,
                'Table Number': record.table_number,
                'Option': record.option,
                'Album Cover': record.album_cover
            }
        }));
    } catch (error) {
        console.error('Error al obtener los registros del DJ:', error);
        throw error;
    }
};

/**
 * Obtiene TODOS los registros de canciones del DJ para Analytics (sin filtrar por estado)
 * @param {string} djViewId - ID de la vista del DJ (form_id)
 * @returns {Promise<Array>} - Arreglo con todos los registros encontrados
 */
export const getAllDJRecords = async (djViewId) => {
    try {
        // Primero obtenemos el ID interno del DJ usando el form_id (djViewId)
        const { data: djData, error: djError } = await supabase
            .from('djs')
            .select('id')
            .eq('form_id', djViewId)
            .maybeSingle();

        if (djError) throw djError;
        if (!djData) throw new Error('DJ no encontrado');

        let allRecords = [];
        let page = 0;
        const pageSize = 1000;
        let hasMore = true;

        while (hasMore) {
            const { data, error } = await supabase
                .from('song_requests')
                .select('id, song_name, artist, genre, created_at, table_number, option, album_cover, dialing_time')
                .eq('dj_id', djData.id)
                .order('created_at', { ascending: false })
                .range(page * pageSize, (page + 1) * pageSize - 1);

            if (error) throw error;

            if (data.length > 0) {
                allRecords = [...allRecords, ...data];
                page++;
                // Si devolvió menos del tamaño de página, es la última página
                if (data.length < pageSize) {
                    hasMore = false;
                }
            } else {
                hasMore = false;
            }
        }

        // Mapeamos para mantener compatibilidad con la estructura de Airtable
        return allRecords.map(record => ({
            id: record.id,
            fields: {
                'Song Name': record.song_name,
                'Artist': record.artist,
                'Genre': record.genre,
                'Created': record.created_at,
                'Table Number': record.table_number,
                'Option': record.option,
                'Album Cover': record.album_cover,
                'Time of request': record.created_at, // Analytics usa este campo
                'Dialing time': record.dialing_time
            }
        }));
    } catch (error) {
        console.error('Error al obtener el historial completo del DJ:', error);
        throw error;
    }
};




/**
 * Crea una solicitud de canción para el DJ
 * @param {string} formId - ID del formulario
 * @param {string} songTitle - Título de la canción
 * @param {string} artistName - Nombre del artista
 * @param {string} genre - Género musical
 * @param {string|null} tableNumber - Número de mesa (opcional)
 * @returns {Promise<Object>} - Registro creado
 */
export const createSongRequest = async (
    formId,
    songTitle,
    artistName,
    genre,
    albumCover,
    tableNumber = null
) => {
    try {
        // Obtener DJ ID
        const { data: djData, error: djError } = await supabase
            .from('djs')
            .select('id')
            .eq('form_id', formId)
            .maybeSingle();

        if (djError) throw djError;
        if (!djData) throw new Error('DJ no encontrado');

        const { data, error } = await supabase
            .from('song_requests')
            .insert([
                {
                    dj_id: djData.id,
                    song_name: songTitle,
                    artist: artistName,
                    genre: genre,
                    album_cover: albumCover,
                    table_number: tableNumber
                }
            ])
            .select();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error al crear la solicitud de canción:', error);
        throw error;
    }
};

/**
 * Guarda una solicitud de canción para el DJ (al mover canciones entre tablas)
 * @param {string} tableId - ID de la tabla destino (form_id)
 * @param {string} songName - Nombre de la canción
 * @param {string} artist - Nombre del artista
 * @param {string} created - Fecha de creación original
 * @param {string} genre - Género musical
 * @param {string} option - Opción adicional
 * @param {string|null} tableNumber - Número de mesa (opcional)
 * @returns {Promise<Object>} - Registro creado
 */
export const saveSongRequest = async (
    tableId,
    songName,
    artist,
    created,
    genre,
    albumCover,
    option = '',
    tableNumber = null
) => {
    try {
        // Obtener DJ ID
        const { data: djData, error: djError } = await supabase
            .from('djs')
            .select('id')
            .eq('form_id', tableId)
            .maybeSingle();

        if (djError) throw djError;
        if (!djData) throw new Error('DJ no encontrado');

        const { data, error } = await supabase
            .from('song_requests')
            .insert([
                {
                    dj_id: djData.id,
                    song_name: songName,
                    artist: artist,
                    genre: genre,
                    album_cover: albumCover,
                    option: option,
                    table_number: tableNumber,
                    // created_at: created, // Supabase maneja created_at automáticamente, pero si queremos preservar la fecha original:
                    dialing_time: new Date().toISOString() // Equivalente a 'Dialing time'
                }
            ])
            .select();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error al guardar la solicitud de canción:', error);
        throw error;
    }
};

/**
 * Obtiene información del DJ
 * @param {string} formId - ID del formulario
 * @returns {Promise<Object>} - Información del DJ
 */
export const getDJInfo = async (formId) => {
    try {
        const { data, error } = await supabase
            .from('djs')
            .select('*')
            .eq('form_id', formId)
            .maybeSingle();

        if (error) throw error;
        if (!data) throw new Error('No se encontró información para este DJ');

        // Mapear a la estructura esperada por el frontend
        return {
            'ID Form': data.form_id,
            'Name': data.name,
            'InstagramLink': data.instagram_link,
            'InstagramHandle': data.instagram_handle,
            'Foto': data.photo_url ? [{ url: data.photo_url }] : [],
            'Logo': data.logo_url ? [{ url: data.logo_url }] : [],
            'LogoPowerBy': data.logo_powered_by_url ? [{ url: data.logo_powered_by_url }] : [],
            'Cuenta Activa': data.is_active
        };
    } catch (error) {
        console.error('Error al obtener la información del DJ:', error);
        throw error;
    }
};

/**
 * Elimina un registro de canción del DJ
 * @param {string} djViewId - ID de la vista del DJ (no usado directamente en delete de Supabase, solo recordId)
 * @param {string} recordId - ID del registro a eliminar
 * @returns {Promise<Object>} - Registro eliminado
 */
export const deleteDJRecord = async (djViewId, recordId) => {
    try {
        const { data, error } = await supabase
            .from('song_requests')
            .delete()
            .eq('id', recordId)
            .select();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error al eliminar el registro del DJ:', error);
        throw error;
    }
};

/**
 * Actualiza la información del DJ
 * @param {string} formId - ID del formulario
 * @param {Object} profileData - Datos del perfil a actualizar
 * @returns {Promise<Object>} - Registro actualizado
 */
export const updateDJInfo = async (formId, profileData) => {
    try {
        const updateFields = {
            name: profileData.name,
            instagram_link: profileData.instagramLink,
            instagram_handle: profileData.instagramHandle,
        };

        if (profileData.foto) updateFields.photo_url = profileData.foto;
        if (profileData.logo) updateFields.logo_url = profileData.logo;
        if (profileData.logoPowerBy) updateFields.logo_powered_by_url = profileData.logoPowerBy;

        // Manejo de nulos explícitos si es necesario, aunque en Supabase update ignora undefined
        // Si queremos borrar, debemos pasar null

        const { data, error } = await supabase
            .from('djs')
            .update(updateFields)
            .eq('form_id', formId)
            .select();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error al actualizar la información del DJ:', error);
        throw error;
    }
};

/**
 * Actualiza el estado de una solicitud de canción
 * @param {string} recordId - ID del registro
 * @param {string} status - Nuevo estado (option)
 * @returns {Promise<Object>} - Registro actualizado
 */
export const updateSongRequestStatus = async (recordId, status) => {
    try {
        const { data, error } = await supabase
            .from('song_requests')
            .update({
                option: status,
                dialing_time: new Date().toISOString()
            })
            .eq('id', recordId)
            .select();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error al actualizar el estado de la canción:', error);
        throw error;
    }
};

/**
 * Suscribe a cambios en las solicitudes de canciones del DJ
 * @param {string} formId - ID del formulario (para buscar el ID del DJ)
 * @param {function} onInsert - Callback para nuevas canciones
 * @param {function} onUpdate - Callback para actualizaciones de estado
 * @returns {Promise<Object>} - Suscripción de Supabase
 */
export const subscribeToDJRequests = async (formId, onInsert, onUpdate) => {
    try {
        // 1. Obtener el ID interno del DJ
        const { data: djData, error: djError } = await supabase
            .from('djs')
            .select('id')
            .eq('form_id', formId)
            .maybeSingle();

        if (djError) throw djError;
        if (!djData) throw new Error('DJ no encontrado para suscripción');

        // 2. Crear la suscripción
        const channel = supabase
            .channel(`public:song_requests:dj_id=eq.${djData.id}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'song_requests',
                    filter: `dj_id=eq.${djData.id}`
                },
                (payload) => {
                    // Solo notificar si es una canción pendiente
                    if (!payload.new.option || payload.new.option === 'pendiente') {
                        onInsert({
                            id: payload.new.id,
                            fields: {
                                'Song Name': payload.new.song_name,
                                'Artist': payload.new.artist,
                                'Genre': payload.new.genre,
                                'Created': payload.new.created_at,
                                'Table Number': payload.new.table_number,
                                'Option': payload.new.option,
                                'Album Cover': payload.new.album_cover
                            }
                        });
                    }
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'song_requests',
                    filter: `dj_id=eq.${djData.id}`
                },
                (payload) => {
                    // Notificar actualización (para remover si cambia de estado)
                    onUpdate({
                        id: payload.new.id,
                        fields: {
                            'Option': payload.new.option
                        }
                    });
                }
            )
            .subscribe();

        return channel;

    } catch (error) {
        console.error('Error al suscribirse a cambios:', error);
        return null;
    }
};
